import store from '../redux/store';
import { AuthUserState, MenuType } from './dto/response';
import { Menus } from './dto/response/common_Type';
const CryptoJS = require('crypto-js');


export function encryptText(plainText: any) {
    if (!plainText) return ''
    let key = CryptoJS.enc.Utf8.parse('3d7de24152e38eb4b56dec9712f0dc1323743e1a2b18a14db72d4f11da705976');
    let iv = CryptoJS.enc.Utf8.parse('e2c84cfc673114b0f037e0fcd82d343d');
    let config = { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
    let encPassword = CryptoJS.AES.encrypt(plainText, key, config);
    return encPassword.ciphertext.toString(CryptoJS.enc.Base64);
}

export const decryptText = (text: any) => {
    if (!text) return ''
    let key = CryptoJS.enc.Utf8.parse('3d7de24152e38eb4b56dec9712f0dc1323743e1a2b18a14db72d4f11da705976');
    let iv = CryptoJS.enc.Utf8.parse('e2c84cfc673114b0f037e0fcd82d343d');
    let config = { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
    try {
        let encPassword = CryptoJS.AES.decrypt(text, key, config);
        return encPassword.toString(CryptoJS.enc.Utf8);
    } catch (err) {
        try {
            return JSON.parse(text)
        } catch (err1) {
            return text || ''
        }
    }
}
export const stableSort = <T>(array: readonly T[], comparator: (a: T, b: T) => number) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export const getComparator = <Key extends keyof any>(order: any, orderBy: Key,): (
    a: { [key in Key]: any },
    b: { [key in Key]: any },
) => number => {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

const descendingComparator = <T>(a: T, b: T, orderBy: keyof T) => {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

export const isLoggedIn = (state: AuthUserState) => {
    const { Token, User, ExpiresAt } = state;
    return !!User && !!Token && !!ExpiresAt;
}




export const formatUserType = (userType?: string) => {
    if (!userType) return "";
    return userType
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};


export const hasAccessToPath = (menus: any, path: string): MenuType | null => {
    if (!menus) return null;
    for (const menu of menus) {
        if (menu.url === path) {
            return menu;
        }

        if (menu.sub_menus && menu.sub_menus.length > 0 && hasAccessToPath(menu.sub_menus, path)) {
            return menu.sub_menus.find((menu: any) => menu.url === path) || null;
        }
    }

    return null;
};

// Recursively collect every non-empty `url` from a menu tree (including nested sub_menus).
const collectMenuUrls = (menus: any[]): string[] =>
    (Array.isArray(menus) ? menus : [])
        .flatMap((item: { url?: string; sub_menus?: any[] | null }) => [
            item?.url ?? '',
            ...(item?.sub_menus ? collectMenuUrls(item.sub_menus) : []),
        ])
        .filter((url: string) => typeof url === 'string' && url.trim() !== '');

const getAllModuleUrls = (_dispatch?: any): string[] => {
    // Derive authorized URLs from the live menu tree, which is refreshed on every
    // login. Relying on a cached `moduleUrls` array meant newly added menu URLs
    // (e.g. a new sub-page) were never recognised until that cache was cleared.
    const menus = store.getState().permission?.menus ?? [];
    if (Array.isArray(menus) && menus.length > 0) {
        return collectMenuUrls(menus);
    }

    // Fallback for a hard refresh before redux is rehydrated: rebuild from the
    // encrypted roles_permissions persisted in localStorage at login.
    try {
        const rolesPermissions = localStorage.getItem('roles_permissions');
        const mainPermissionsObject = rolesPermissions ? JSON.parse(decryptText(rolesPermissions)) : {};
        return mainPermissionsObject.menus ? collectMenuUrls(mainPermissionsObject.menus) : [];
    } catch (error) {
        console.error("Error fetching module URLs from local storage:", error);
        return [];
    }
};


export const isAuthorizedPage = (dispatch: any, location: any): boolean => {
    const currentPath = location.pathname.split('?')[0];
    const moduleUrls = getAllModuleUrls(dispatch);
    return moduleUrls.includes(currentPath);
};

export function filterNavigableMenus(menuData: Menus) {
    if (!Array.isArray(menuData)) return [];

    const byMenuOrder = (a: any, b: any) =>
        (Number(a?.menu_order) || 0) - (Number(b?.menu_order) || 0);

    const filtered = menuData.reduce((acc, item) => {
        const { is_navigation, permissions, sub_menus } = item || {};

        if (is_navigation !== 1 || !Array.isArray(permissions) || permissions.length === 0) {
            return acc;
        }

        let validSubMenus = null;

        if (Array.isArray(sub_menus)) {
            validSubMenus = sub_menus
                .filter(sub =>
                    sub?.is_navigation === 1 &&
                    Array.isArray(sub.permissions) &&
                    sub.permissions.length > 0
                )
                .sort(byMenuOrder);

            if (validSubMenus.length === 0) {
                validSubMenus = null;
            }
        }

        acc.push({ ...item, sub_menus: validSubMenus });
        return acc;
    }, []);

    return filtered.sort(byMenuOrder);
}
type MenuItem = {
    id: number;
    uuid: string;
    name: string;
    slug: string;
    icon: string | null;
    parent_id: number | null;
    url: string | null;
    is_navigation: number;
    menu_order: number | string;
};

type NavigationItem = {
    id: number;
    name: string;
    is_navigation: number;
    icon: string | null;
    url: string | null;
    uuid: string;
    subMenus: NavigationItem[];
    menu_order: number | string;
    parent_id: number | null;
};

export function buildNavigationHierarchy(flatList: MenuItem[]): NavigationItem[] {
    const map = new Map<number, NavigationItem>();
    const roots: NavigationItem[] = [];
    for (const item of flatList) {
        const id = Number(item.id);
        const navItem: NavigationItem = {
            id: item.id,
            name: item.name,
            icon: item.icon ?? null,
            url: item.url ?? null,
            uuid: item.uuid,
            subMenus: [],
            menu_order: item.menu_order,
            parent_id: item.parent_id,
            is_navigation: item.is_navigation ?? null,
        };
        map.set(id, navItem);
    }
    for (const item of flatList) {
        const id = Number(item.id);
        const parentId = Number(item.parent_id);
        const current = map.get(id);
        const parent = map.get(parentId);
        if (item.parent_id && parentId !== 0 && parent) {
            parent.subMenus.push(current!);
        } else {
            roots.push(current!);
        }
    }
    const sortByOrder = (items: NavigationItem[]) => {
        items.sort((a, b) => a.id - b.id);
        for (const item of items) {
            if (item.subMenus.length > 0) {
                sortByOrder(item.subMenus);
            }
        }
    };
    sortByOrder(roots);
    return roots;
}


export async function aesEncrypt(plainText: string, secretKey: string): Promise<string> {
    const enc = new TextEncoder();

    // 1️⃣ Derive 256-bit key from secret
    const keyMaterial = await crypto.subtle.importKey(
        "raw",
        enc.encode(secretKey),
        { name: "PBKDF2" },
        false,
        ["deriveKey"]
    );

    const salt = enc.encode("static-salt"); // MUST be same in Go
    const key = await crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt,
            iterations: 100000,
            hash: "SHA-256",
        },
        keyMaterial,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt"]
    );

    // 2️⃣ Generate IV (nonce)
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // 3️⃣ Encrypt
    const encrypted = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        enc.encode(plainText)
    );

    // 4️⃣ Combine IV + ciphertext
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    // 5️⃣ Base64 encode
    return btoa(String.fromCharCode(...combined));
}